// Mode-aware coverage helpers for the output-formula-picker consumers
// (create-outputs.component and create-outputs-collection.component).
//
// Range-mode coverage is media-based and stays in the picker itself
// (aggregateMediaCoverage / describeMediaCoverage in
// output-formula-picker.component.ts). EV-mode coverage requires host-side
// context (the set of selected items' EV ids + the loaded convention entries)
// so it lives here — the picker doesn't know about products.

export interface EvConventionEntry {
  masterSuiteEnhancementVariantId: string;
}

export interface AggregateEvCoverageInput {
  selectedEvIds: readonly string[];
  formulaEvConventionIds: readonly string[];
  evConventionEntriesById: ReadonlyMap<string, readonly EvConventionEntry[]>;
}

export interface EvCoverageResult {
  coveredEvIds: string[];
  missingEvIds: string[];
}

export interface DescribeEvCoverageInput {
  coveredEvIds: readonly string[];
  missingEvIds: readonly string[];
  hasItems: boolean;
  hasFormula: boolean;
}

const conventionCoversEv = (
  conventionId: string,
  evId: string,
  evConventionEntriesById: ReadonlyMap<string, readonly EvConventionEntry[]>,
): boolean => {
  const entries = evConventionEntriesById.get(conventionId) ?? [];
  return entries.some((entry) => entry.masterSuiteEnhancementVariantId === evId);
};

const firstMatchingConventionCoversEv = (
  evId: string,
  formulaEvConventionIds: readonly string[],
  evConventionEntriesById: ReadonlyMap<string, readonly EvConventionEntry[]>,
): boolean =>
  formulaEvConventionIds.some((conventionId) =>
    conventionCoversEv(conventionId, evId, evConventionEntriesById),
  );

export const aggregateEvCoverage = (
  input: AggregateEvCoverageInput,
): EvCoverageResult => {
  const coveredEvIds = input.selectedEvIds.filter((evId) =>
    firstMatchingConventionCoversEv(
      evId,
      input.formulaEvConventionIds,
      input.evConventionEntriesById,
    ),
  );
  const missingEvIds = input.selectedEvIds.filter(
    (evId) =>
      !firstMatchingConventionCoversEv(
        evId,
        input.formulaEvConventionIds,
        input.evConventionEntriesById,
      ),
  );
  return { coveredEvIds, missingEvIds };
};

export const describeEvCoverage = (input: DescribeEvCoverageInput): string => {
  if (!input.hasItems) {
    return 'Coverage: no items selected';
  }
  if (!input.hasFormula) {
    return 'Coverage: no EV formula picked';
  }
  const total = input.coveredEvIds.length + input.missingEvIds.length;
  const covered = input.coveredEvIds.length;
  if (input.missingEvIds.length === 0) {
    return `Coverage: ${covered}/${total} covered`;
  }
  const missingList = input.missingEvIds.join(', ');
  return `Coverage: ${covered}/${total} covered, ${input.missingEvIds.length} missing: ${missingList}`;
};
